import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';

import { useModalsStore } from '../../modals.store';
import { ConfirmModalV2Props } from '../../types';
import { ModalContainerV2 } from '../interwill/containers/ModalContainerV2';

export const ConfirmModalV2: FC<ConfirmModalV2Props> = ({
  title,
  bodyText,
  confirmText,
  cancelText,
  onConfirm,
  variant = 'confirm',
}) => {
  const modals = useModalsStore();
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'confirmModalV2' });
  const [isLoading, setIsLoading] = useState(false);

  const modalTitle = title || t('deleteTitle');
  const modalBody = bodyText || t('deleteText');
  const cancelLabel = cancelText || t('cancel');
  const confirmLabel = confirmText || t(variant === 'delete' ? 'delete' : 'confirm');

  const handleCancel = () => {
    modals.closeModal();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
    modals.closeModal();
  };

  return (
    <ModalContainerV2 pinBottom title={modalTitle}>
      <Body>{modalBody}</Body>
      <ButtonWrapper>
        <Button size="middle" type="default" onClick={handleCancel}>
          {cancelLabel}
        </Button>
        <ActionButton
          className={cn(variant)}
          size="middle"
          type="primary"
          onClick={handleConfirm}
          loading={isLoading}
        >
          {confirmLabel}
        </ActionButton>
      </ButtonWrapper>
    </ModalContainerV2>
  );
};

const Body = styled.p`
  padding-block: 24px;
  color: ${COLORS.systemDark300};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 16px;
`;

const ActionButton = styled(Button)`
  &&& {
    &.delete {
      background: ${COLORS.systemFlame200};
    }

    &.delete:hover {
      background: ${COLORS.systemFlame300};
    }
  }
`;
